import * as THREE from 'three'
import { Side, front, top, right, bottom, left, back } from 'src/constants/sides'
import {
  ReferencePoint,
  ReferencePoints,
  topLeft,
  topCenter,
  topRight,
  centerLeft,
  centerCenter,
  centerRight,
  bottomLeft,
  bottomCenter,
  bottomRight
} from 'src/constants/referencePoints'
import { getOctants, referencePointMap } from './getOctant'
import { upperFirst } from 'lodash'

type MinimalBox2 = { [key in 'min' | 'max']: { [key in 'x' | 'y']: number } }
type PointName = Exclude<Side, 'back' | 'front'> | 'center'
type PointPositions = { [key in ReferencePoint]: [number, number] }
type PointPart = [PointName, number]

export const getPointPositions = ({ min, max }: MinimalBox2): PointPositions => {
  const left: PointPart = ['left', min.x],
    right: PointPart = ['right', max.x],
    top: PointPart = ['top', max.y],
    bottom: PointPart = ['bottom', min.y]
  const center_h: PointPart = ['center', (max.y + min.y) / 2]
  const center_v: PointPart = ['center', (max.x + min.x) / 2]
  return [top, center_h, bottom].reduce(
    (prevVertical, currentVertical) => ({
      ...prevVertical,
      ...[left, center_v, right].reduce(
        (prevHorizontal, currentHorizontal) => ({
          ...prevHorizontal,
          [`${currentVertical[0]}${upperFirst(currentHorizontal[0])}` as ReferencePoint]: [
            currentHorizontal[1],
            currentVertical[1]
          ]
        }),
        {} as Partial<PointPositions>
      ) //  [v, current] as [number, number])]
    }),
    {} as PointPositions
  )
}

type ReferencePoints3D = { [key in Side]: ReferencePoints<[number, number, number]> }

export function getReferencePoints(boundings: THREE.Box3): ReferencePoints3D {
  const octants = getOctants(boundings)
  return Object.entries(referencePointMap).reduce(
    (p, c) => ({
      ...p,
      [c[0]]: Object.entries(c[1]).reduce((p, c) => ({ ...p, [c[0]]: octants[c[1]] }), {})
    }),
    {}
  ) as ReferencePoints3D
}

export function getReferencePoints2D(boundings: THREE.Box2): ReferencePoints<[number, number]> {
  const { min, max } = boundings
  const center = new THREE.Vector2()
  boundings.getCenter(center)
  const { x, y } = center
  return {
    [topLeft]: [min.x, max.y],
    [topCenter]: [x, max.y],
    [topRight]: [max.x, max.y],
    [centerLeft]: [min.x, y],
    [centerCenter]: [x, y],
    [centerRight]: [max.x, y],
    [bottomLeft]: [min.x, min.y],
    [bottomCenter]: [x, min.y],
    [bottomRight]: [max.x, min.y]
  }
}
